/**
 * Which saved conversation belongs to which workflow, read from its work
 * settings. The page's rules are in `cards.ts`, kept free of imports so they can
 * be tested on their own.
 */

import { useEffect, useState } from "react";
import type { WorkflowListing } from "../../../shared/workflow-listing";
import { openWorkflow } from "./cards";
import { lookupThreadWorkflow, type SettingsScope } from "./work-settings/api";
import type { WorkflowListState } from "./useWorkflowSections";

export {
  DEFAULT_WORKFLOW_SORT,
  WORKFLOW_CATEGORIES,
  WORKFLOW_SECTION_ORDER,
  defaultSection,
  listWorkflows,
  openWorkflow,
  type OpenWorkflow,
  type WorkflowCategory,
  type WorkflowPhase,
  type WorkflowSectionId,
  type WorkflowSort,
} from "./cards";

/** One conversation, as the lookup and its memory key it. */
function threadKey(scope: { orgId: string; projectId: string; userId: string; threadId: string | null }): string {
  return JSON.stringify([scope.orgId, scope.projectId, scope.userId, scope.threadId]);
}

/**
 * What each conversation was started in, once known: read from its settings, or
 * recorded when the workflow's view starts it. Kept for the life of the page, so
 * coming back to a conversation shows its own view at once rather than an
 * ordinary chat until the read returns. A conversation's workflow never changes.
 */
const known = new Map<string, string | null>();

/**
 * The conversation `scope` names was just started from `workflowId`'s view, or
 * — `null` — as an ordinary chat. A conversation this page started needs no
 * lookup: until one answers, the pane hides the conversation behind "Loading...".
 */
export function rememberThreadWorkflow(
  scope: { orgId: string; projectId: string; userId: string; threadId: string },
  workflowId: string | null,
): void {
  known.set(threadKey(scope), workflowId);
}

/** A saved conversation's workflow (by code, as its snapshot names it), and whether this agent can still run it. */
export interface ThreadWorkflow {
  code: string;
  /** Listed as open by the agent. False: the conversation is readable, not continuable. */
  open: boolean;
}

/**
 * The workflow a saved conversation was started in, read from its work settings
 * snapshot (`GET /workflows/threads/:threadId`). Null for an ordinary chat;
 * `undefined` while the list or the first read for this conversation is still
 * out, so the pane can wait rather than draw it as a chat.
 *
 * **Which workflow a conversation is and whether it can run now are two answers.**
 * The first comes from the lookup alone and never changes; the list only says
 * `open`. A list that could not be read, or one with every workflow closed, makes
 * a workflow's conversation not continuable — never an ordinary chat, whose turns
 * go to `/ask`.
 */
export function useThreadWorkflow(scope: SettingsScope | null, list: WorkflowListState): ThreadWorkflow | null | undefined {
  const [, setRead] = useState(0);
  // A lookup that failed is asked again shortly: the answer decides where the
  // conversation's turns go, so until it is known the conversation is not drawn
  // as a chat — a chat would send to `/ask`, the wrong place for a workflow's.
  const [attempt, setAttempt] = useState(0);
  const listed: readonly WorkflowListing[] = list.kind === "ok" ? list.workflows : [];
  const threadId = scope?.threadId ?? null;
  const key = scope ? threadKey(scope) : "";
  const token = scope?.token;
  useEffect(() => {
    // The agent answers the lookup whatever the workflows' states, so it is asked
    // whatever the list says.
    if (!scope || !threadId || known.has(key)) return;
    const controller = new AbortController();
    let retry: number | undefined;
    lookupThreadWorkflow({ ...scope, threadId }, controller.signal)
      .then((workflowId) => {
        known.set(key, workflowId);
        setRead((n) => n + 1);
      })
      .catch(() => {
        if (!controller.signal.aborted) retry = window.setTimeout(() => setAttempt((n) => n + 1), LOOKUP_RETRY_MS);
      });
    return () => {
      controller.abort();
      window.clearTimeout(retry);
    };
    // `scope` is rebuilt every render; `key` and `token` stand for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token, attempt]);
  if (!scope || !threadId) return null;
  if (!known.has(key)) return undefined;
  const code = known.get(key);
  if (!code) return null;
  // Whether it is open is not known yet: wait, rather than say it cannot continue.
  if (list.kind === "loading") return undefined;
  return { code, open: openWorkflow(code, listed) !== null };
}

/** How long after a failed lookup the conversation's workflow is asked for again. */
const LOOKUP_RETRY_MS = 3000;
